/**
 * HeroCarousel — lightweight, dependency-free hero carousel.
 *
 * Replaces Splide on the Kingz Kimonos hero. Uses native CSS scroll-snap
 * for movement (no JS transforms, no DOM clones) so the browser handles
 * touch/swipe, momentum and smooth scrolling. JS only adds dot navigation,
 * keyboard support, active-dot syncing and a deferred auto-advance timer.
 *
 * Expected markup (rendered by the Liquid section):
 *   <div class="hero-carousel" data-hero-carousel
 *        data-autoplay="true" data-interval="5000">
 *     <div class="hero-track" role="group" aria-label="Hero slides">
 *       <div class="hero-slide">…</div>
 *       <div class="hero-slide">…</div>
 *       <div class="hero-slide">…</div>
 *     </div>
 *     <div class="hero-nav" role="tablist"></div>  <!-- dots injected here -->
 *   </div>
 *
 * Performance notes:
 *   - Instances are created after DOMContentLoaded.
 *   - Auto-advance is deferred until window.load + 2s so it never competes
 *     with the LCP hero paint.
 *   - Scroll handling is debounced via requestAnimationFrame.
 */
class HeroCarousel {
  constructor(element) {
    this.el = element;
    this.track = element.querySelector('.hero-track');
    this.slides = this.track ? Array.from(this.track.querySelectorAll('.hero-slide')) : [];
    this.nav = element.querySelector('.hero-nav');
    this.dots = [];

    this.currentIndex = 0;
    this.autoplay = element.dataset.autoplay !== 'false';
    this.interval = parseInt(element.dataset.interval, 10) || 5000;
    this.autoAdvanceId = null;
    this.scrollRaf = null;
    this.userPaused = false;

    // Bound handlers so we can add/remove them cleanly.
    this._onScroll = this._onScroll.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._pause = this.stopAutoAdvance.bind(this);
  }

  init() {
    if (!this.track || this.slides.length <= 1) return;

    this._buildDots();
    this._bindEvents();

    // Defer auto-advance until well after the LCP window so it never
    // interferes with the first hero paint.
    if (this.autoplay) {
      window.addEventListener('load', () => {
        window.setTimeout(() => this.startAutoAdvance(), 2000);
      }, { once: true });
    }
  }

  /* ---------- Setup ---------- */

  _buildDots() {
    if (!this.nav) return;

    this.dots = this.slides.map((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'hero-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      if (i === 0) dot.classList.add('is-active');

      dot.addEventListener('click', () => {
        this.stopAutoAdvance();
        this.scrollToSlide(i);
      });

      this.nav.appendChild(dot);
      return dot;
    });
  }

  _bindEvents() {
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    // Any manual interaction cancels auto-advance for the session.
    ['pointerdown', 'touchstart', 'wheel'].forEach((evt) => {
      this.track.addEventListener(evt, this._pause, { passive: true });
    });

    // Keyboard navigation (arrow keys) when the carousel has focus.
    this.el.addEventListener('keydown', this._onKeydown);

    // Pause when the tab is hidden, resume when visible again.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoAdvance();
      } else if (this.autoplay && !this.userPaused) {
        this.startAutoAdvance();
      }
    });
  }

  /* ---------- Navigation ---------- */

  scrollToSlide(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[clamped];
    if (!slide) return;

    // scrollTo with left offset keeps scroll-snap happy and avoids
    // scrolling the whole page (scrollIntoView can do that).
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this._setActive(clamped);
  }

  _setActive(index) {
    if (index === this.currentIndex) return;
    this.currentIndex = index;

    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  /* ---------- Event handlers ---------- */

  _onScroll() {
    // Debounce with rAF: coalesce a burst of scroll events into one update.
    if (this.scrollRaf) return;
    this.scrollRaf = window.requestAnimationFrame(() => {
      this.scrollRaf = null;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this._setActive(Math.max(0, Math.min(index, this.slides.length - 1)));
    });
  }

  _onKeydown(e) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.stopAutoAdvance();
      this.scrollToSlide(this.currentIndex + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.stopAutoAdvance();
      this.scrollToSlide(this.currentIndex - 1);
    }
  }

  /* ---------- Auto-advance ---------- */

  startAutoAdvance() {
    if (!this.autoplay || this.autoAdvanceId) return;
    this.autoAdvanceId = window.setInterval(() => {
      const next = (this.currentIndex + 1) % this.slides.length;
      this.scrollToSlide(next);
    }, this.interval);
  }

  stopAutoAdvance() {
    this.userPaused = true;
    if (this.autoAdvanceId) {
      window.clearInterval(this.autoAdvanceId);
      this.autoAdvanceId = null;
    }
  }
}

/* Auto-initialize every carousel on the page after the DOM is ready. */
function initHeroCarousels() {
  document.querySelectorAll('[data-hero-carousel]').forEach((el) => {
    new HeroCarousel(el).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroCarousels, { once: true });
} else {
  initHeroCarousels();
}

/*
 * Theme editor support: when a merchant edits, reorders or reloads the
 * slideshow section, Shopify re-renders its markup and fires
 * `shopify:section:load`. Re-initialize the affected carousel so the dots
 * and auto-advance work in the live preview. (No-op on the storefront.)
 */
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('[data-hero-carousel]');
    if (carousel) new HeroCarousel(carousel).init();
  });
}

// Expose for manual instantiation / theme editor re-init if needed.
window.HeroCarousel = HeroCarousel;
